/**
 * Signal glyphs: what a condition waits for and what an operation does, drawn
 * as the trace a scope would show rather than said in words. A dashed line is
 * the threshold; the solid line the reading or the rate.
 *
 * Decorative: whatever shows one also says it in words, as its accessible
 * name or its title.
 */
import type { ReactNode } from "react";
import type { Comparison, Condition, Profile, RunPump } from "../design/protocol";
import { profileValues, secondsText } from "../scripts/operations";

export type SignalShape =
  // A condition: met on a level, or on crossing into it.
  | "above"
  | "below"
  | "rise"
  | "fall"
  // Its qualifiers: held met that long; blips shorter than that ignored; its fall from its peak.
  | "hold"
  | "ignore"
  | "drop"
  // Other ways a step ends.
  | "volume"
  | "operator"
  | "clock"
  | "done"
  // What a pump does.
  | "off"
  | "run"
  | "pulse"
  // A component's state and a port's status, in the step table.
  | "valve_open"
  | "valve_closed"
  | "connected"
  | "disconnected"
  | "sealed"
  | "pressure"
  | "vacuum"
  | "through";

const THRESHOLD = <path d="M1 6H17" strokeDasharray="1.5 1.5" opacity={0.5} />;

const PATHS: Record<SignalShape, ReactNode> = {
  above: (
    <>
      {THRESHOLD}
      <path d="M1 2.5H17" />
    </>
  ),
  below: (
    <>
      {THRESHOLD}
      <path d="M1 9.5H17" />
    </>
  ),
  rise: (
    <>
      {THRESHOLD}
      <path d="M1 9.5H8V2.5H17" />
    </>
  ),
  fall: (
    <>
      {THRESHOLD}
      <path d="M1 2.5H8V9.5H17" />
    </>
  ),
  hold: <path d="M2 3V9M2 6H16M16 3V9" />,
  ignore: (
    <>
      <path d="M1 9.5H7V3H10V9.5H17" />
      <path d="M4 11.5L13 0.5" opacity={0.6} />
    </>
  ),
  drop: (
    <>
      <path d="M1 10L6 2.5L17 8" />
      <path d="M6 2.5H17" strokeDasharray="1.5 1.5" opacity={0.5} />
    </>
  ),
  volume: <path d="M9 1.5C9 1.5 4.5 6.5 4.5 8.2A4.5 3.8 0 0 0 13.5 8.2C13.5 6.5 9 1.5 9 1.5Z" />,
  operator: (
    <>
      <circle cx={9} cy={3.5} r={2.2} />
      <path d="M3.5 11.5C4.5 8 13.5 8 14.5 11.5" />
    </>
  ),
  clock: (
    <>
      <circle cx={9} cy={6} r={5} />
      <path d="M9 3.2V6H11.4" />
    </>
  ),
  done: <path d="M1 6H13M10 3L13 6L10 9M16 2V10" />,
  off: <path d="M1 9.5H17" opacity={0.6} />,
  run: <path d="M1 9.5H3V2.5H17M14 0.5L17 2.5L14 4.5" />,
  pulse: <path d="M1 9.5H3V2.5H14V9.5H17" />,
  // A valve's state, as the step table reads it: open, an unmarked way through;
  // closed, the same circle crossed. The drawing has its own pinch-valve symbol
  // (render/glyphs.tsx); these two are only ever drawn in the table.
  valve_open: <circle cx={9} cy={6} r={4.7} />,
  valve_closed: (
    <>
      <circle cx={9} cy={6} r={4.7} />
      <path d="M5.7 2.7L12.3 9.3M12.3 2.7L5.7 9.3" />
    </>
  ),
  // A connector's halves: made, one body on the tube; unmade, apart.
  connected: <path d="M1 6H4M14 6H17M4 3H14V9H4ZM9 3V9" />,
  disconnected: <path d="M1 6H4M4 3H7.5V9H4ZM10.5 3H14V9H10.5ZM14 6H17" />,
  // A port: capped; pressure in; vacuum out; a connection through.
  sealed: <path d="M1 6H12M12.5 2.5V9.5M15 2.5V9.5" />,
  pressure: (
    <>
      <circle cx={9} cy={6} r={4.7} />
      <path d="M6.5 6H11.5M9 3.5V8.5" />
    </>
  ),
  vacuum: (
    <>
      <circle cx={9} cy={6} r={4.7} />
      <path d="M6.5 6H11.5" />
    </>
  ),
  through: <path d="M1 6H17M3.5 4V8M14.5 4V8" />,
};

function Svg({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      className={`sx-glyph${className ? ` ${className}` : ""}`}
      viewBox="0 0 18 12"
      width={18}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function SignalGlyph({ shape }: { shape: SignalShape }) {
  return <Svg>{PATHS[shape]}</Svg>;
}

/** A profile or a ramp as its own trace, scaled to the glyph: a staircase looks like one. */
export function TraceGlyph({ points }: { points: readonly (readonly [number, number])[] }) {
  if (points.length < 2) return <SignalGlyph shape="run" />;
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  const x = (value: number) => 1 + (x1 > x0 ? ((value - x0) / (x1 - x0)) * 16 : 0);
  const y = (value: number) => 10 - (y1 > y0 ? ((value - y0) / (y1 - y0)) * 8 : 4);
  const d = points.map(([px, py], i) => `${i ? "L" : "M"}${x(px).toFixed(2)} ${y(py).toFixed(2)}`).join("");
  return (
    <Svg>
      <path d={d} />
    </Svg>
  );
}

/** A trace of a pump's ramp: rate over time. */
function rampTrace(segments: readonly { from: number; to: number; seconds: number }[]): [number, number][] {
  const points: [number, number][] = [];
  let t = 0;
  for (const segment of segments) {
    points.push([t, segment.from]);
    t += segment.seconds;
    points.push([t, segment.to]);
  }
  return points;
}

/** A source's profile as the instrument runs it: a staircase's levels, each held, or its ramp. */
export function ProfileGlyph({ profile }: { profile: Profile }) {
  if (profile.mode === "ramp")
    return <TraceGlyph points={rampTrace((profile.segments ?? []).map((s) => ({ from: s.from_value, to: s.to_value, seconds: s.seconds })))} />;
  const levels = profileValues(profile);
  const points: [number, number][] = [];
  levels.forEach((level, i) => points.push([i, level], [i + 1, level]));
  // Vacuum stairs go down: drawn by magnitude, so a deeper vacuum is higher.
  return <TraceGlyph points={points.map(([t, v]) => [t, profile.quantity === "vacuum" ? -v : v])} />;
}

/** What a pump does, as its rate would trace: off, running on, a pulse that ends (a volume or a time), a ramp. */
export function PumpGlyph({ pump }: { pump: RunPump }) {
  switch (pump.mode) {
    case "off":
      return <SignalGlyph shape="off" />;
    case "run":
      return <SignalGlyph shape="run" />;
    case "volume":
    case "time":
      return <SignalGlyph shape={pump.keep_running ? "run" : "pulse"} />;
    case "ramp":
      return <TraceGlyph points={rampTrace((pump.segments ?? []).map((s) => ({ from: s.from_rate, to: s.to_rate, seconds: s.seconds })))} />;
  }
}

// --- Conditions --------------------------------------------------------------------

const rises = (comparison: Comparison) => comparison === "gt" || comparison === "ge";

/** A condition's trace: met on a level (above or below its threshold), or on crossing into it. */
export function triggerShape(condition: Condition): SignalShape | null {
  if (condition.kind !== "sensed" && condition.kind !== "pressure_threshold") return null;
  const up = rises(condition.comparison);
  if (condition.edge) return up ? "rise" : "fall";
  return up ? "above" : "below";
}

/** A hold and an ignore, each a glyph and its time. */
export function TriggerQualifiers({ hold, ignore }: { hold?: number | null; ignore?: number | null }) {
  return (
    <>
      {hold ? (
        <span className="sx-glyph-part">
          <SignalGlyph shape="hold" />
          {secondsText(hold)}
        </span>
      ) : null}
      {ignore ? (
        <span className="sx-glyph-part">
          <SignalGlyph shape="ignore" />
          {`<${secondsText(ignore)}`}
        </span>
      ) : null}
    </>
  );
}
